import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import ReactGA from "react-ga4";
import Seo from "../../components/Seo";

function FAQSchemaMarkup({ faqs }) {
  useEffect(() => {
    if (!faqs?.length) return;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.schemaAnswer || faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [faqs]);

  return null;
}

export default function FAQ() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/faq", title: "FAQ" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const faqRow = "rgba(255,255,255,0.03)";

  const faqs = useMemo(
    () => [
      {
        question: "How do I install my plugin?",
        answer:
          "Download the installer from your My Gear page, close your DAW before installing, run the installer, then open the plugin in your DAW and sign in with your email and password to unlock it.",
        richAnswer: (
          <Stack spacing={1.25}>
            <Typography sx={{ color: style.color3 }}>
              We recommend closing your DAW or host program before installation.
            </Typography>

            <Box
              component="ol"
              sx={{
                pl: 2.5,
                m: 0,
                color: style.color3,
                "& li": { mb: 1.25 },
              }}
            >
              <li>
                Download the installer from the{" "}
                <Box
                  component={RouterLink}
                  to="/my-gear"
                  sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
                >
                  My Gear
                </Box>{" "}
                page.
              </li>
              <li>
                Run the installer. It will place the plugin in the correct
                location for your DAW to find it.
              </li>
              <li>
                Open the plugin inside your DAW and sign in with your email and
                password to unlock it.
              </li>
              <li>Start creating.</li>
            </Box>
          </Stack>
        ),
      },
      {
        question: "Can I use the plugin on multiple devices?",
        answer:
          "Yes. Each license allows up to 5 installations, and you can manage your devices from the My Gear section of your account.",
        richAnswer: (
          <Stack spacing={1.25}>
            <Typography sx={{ color: style.color3 }}>
              Yes. Each license allows up to 5 installations by default.
            </Typography>
            <Typography sx={{ color: style.color3 }}>
              You can manage your devices from the{" "}
              <Box
                component={RouterLink}
                to="/my-gear"
                sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
              >
                My Gear
              </Box>{" "}
              section of your account.
            </Typography>
            <Typography sx={{ color: style.color3 }}>
              If you need a higher device limit for a legitimate use case, contact{" "}
              <Box
                component="a"
                href="mailto:support@parishaudio.com"
                sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
              >
                support@parishaudio.com
              </Box>
              .
            </Typography>
          </Stack>
        ),
      },
      {
        question: "Do you offer a free trial?",
        answer:
          "Yes. Parish Audio offers a free 7 day trial through the monthly subscription, and you can cancel at any time.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            Yes. We offer a free 7 day trial when you start a monthly subscription.
            You can cancel anytime.
          </Typography>
        ),
      },
      {
        question: "What DAWs are supported?",
        answer:
          "Parish Audio plugins work in major DAWs that support VST3, including Ableton Live, FL Studio, Cubase, and Reaper.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            Our plugins work in major DAWs that support VST3, including Ableton
            Live, FL Studio, Cubase, and Reaper.
          </Typography>
        ),
      },
      {
        question: "Are your plugins compatible with both Windows and Mac?",
        answer:
          "Yes. Parish Audio plugins are available for both Windows and Mac.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            Yes. All of our plugins are available for both Windows and Mac.
          </Typography>
        ),
      },
      {
        question: "How do downloads work?",
        answer:
          "Once you have purchased a plugin or started a subscription, you can download the installer from the product page or your account and then sign in inside the plugin to unlock it.",
        richAnswer: (
          <Stack spacing={1.25}>
            <Typography sx={{ color: style.color3 }}>
              Once you have purchased a plugin or started a subscription, you can
              download the installer from the product page or your account.
            </Typography>
            <Typography sx={{ color: style.color3 }}>
              After installation, open the plugin in your DAW and sign in with
              your account to unlock it.
            </Typography>
          </Stack>
        ),
      },
      {
        question: "How do I cancel my subscription?",
        answer:
          "You can manage or cancel your subscription from the Manage Subscriptions page in your account.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            You can manage or cancel your subscription on the{" "}
            <Box
              component={RouterLink}
              to="/manage-subscriptions"
              sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
            >
              Manage Subscriptions
            </Box>{" "}
            page.
          </Typography>
        ),
      },
      {
        question: "If I cancel my subscription, do I immediately lose access to my plugins?",
        answer:
          "No. If you cancel your subscription, your plugins remain available until the end of the billing period. You also keep access to plugins you purchased separately.",
        richAnswer: (
          <Box
            component="ul"
            sx={{
              pl: 2.5,
              m: 0,
              color: style.color3,
              "& li": { mb: 1.25 },
            }}
          >
            <li>
              If you cancel your subscription, your plugins remain available
              until the end of your current billing period.
            </li>
            <li>
              You do not lose access to any plugins you purchased separately.
            </li>
          </Box>
        ),
      },
      {
        question: "Can I use my plugin without an internet connection?",
        answer:
          "Yes, with a few exceptions. An internet connection is required for first-time activation, when signing back in after logging out, and for subscription verification at the start of each billing period.",
        richAnswer: (
          <Stack spacing={1.25}>
            <Typography sx={{ color: style.color3 }}>
              Yes, with a few exceptions:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2.5,
                m: 0,
                color: style.color3,
                "& li": { mb: 1.25 },
              }}
            >
              <li>
                An internet connection is required the first time you activate
                and unlock the plugin.
              </li>
              <li>
                If you manually log out, you will need an internet connection to
                sign back in.
              </li>
              <li>
                For subscriptions, the plugin needs to verify your subscription
                at the start of each billing period. After that, it can work
                offline until the next billing period.
              </li>
            </Box>
          </Stack>
        ),
      },
      {
        question: "How do I get updates?",
        answer:
          "You can download the latest version from your account, and update availability may also be announced by email.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            You can download the latest version from your account. When updates
            are available, you may also receive an email notification.
          </Typography>
        ),
      },
      {
        question: "What if my DAW does not see the plugin after installation?",
        answer:
          "Make sure the installer finished successfully, restart your DAW, and rescan for plugins if your DAW supports plugin rescanning.",
        richAnswer: (
          <Stack spacing={1.25}>
            <Typography sx={{ color: style.color3 }}>
              First, make sure the installer completed successfully.
            </Typography>
            <Typography sx={{ color: style.color3 }}>
              Then restart your DAW and run a plugin rescan if your DAW supports
              it.
            </Typography>
            <Typography sx={{ color: style.color3 }}>
              If you still have trouble, contact support and we will help you
              troubleshoot.
            </Typography>
          </Stack>
        ),
      },
      {
        question: "How do I contact support?",
        answer:
          "You can reach Parish Audio support through the contact page or by emailing support@parishaudio.com.",
        richAnswer: (
          <Typography sx={{ color: style.color3 }}>
            You can reach us through the{" "}
            <Box
              component={RouterLink}
              to="/contact"
              sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
            >
              contact page
            </Box>{" "}
            or by emailing{" "}
            <Box
              component="a"
              href="mailto:support@parishaudio.com"
              sx={{ color: style.color4, fontWeight: 700, textDecoration: "none" }}
            >
              support@parishaudio.com
            </Box>
            .
          </Typography>
        ),
      },
    ],
    [style.color3, style.color4]
  );

  const quickLinks = [
    { label: "Browse Plugins", to: "/plugins" },
    { label: "My Gear", to: "/my-gear" },
    { label: "Support", to: "/support" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <>
      <Seo
        title="FAQ | Parish Audio"
        description="Find answers to common questions about Parish Audio plugins, downloads, activation, subscriptions, compatibility, and account management."
        canonical="https://parishaudio.com/faq"
      />

      <FAQSchemaMarkup faqs={faqs} />

      <Box sx={{ width: "95%", margin: "auto", minHeight: height - 140 }}>
        <Paper
          sx={{
            maxWidth: "1100px",
            margin: "24px auto",
            px: isMobile ? 2 : 4,
            py: isMobile ? 2 : 3,
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Box
            ref={topContainer}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.2fr) minmax(280px, 0.8fr)" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box>
              <Stack spacing={1.25}>
                <Chip
                  label="Help Center"
                  sx={{
                    alignSelf: "flex-start",
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.6rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    color: style.color4,
                  }}
                >
                  Frequently Asked Questions
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    fontSize: { xs: "1rem", md: "1.08rem" },
                    maxWidth: 700,
                    lineHeight: 1.65,
                  }}
                >
                  Find answers about installation, downloads, compatibility,
                  subscriptions, activation, and account management for Parish
                  Audio plugins.
                </Typography>
              </Stack>
            </Box>

            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                background: style.darkerPanel,
                boxShadow: 8,
                border: `1px solid rgba(255,255,255,0.08)`,
                backdropFilter: "blur(8px)",
              }}
            >
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 700, color: style.color4 }}>
                  Quick links
                </Typography>

                {quickLinks.map((item) => (
                  <Button
                    key={item.to}
                    component={RouterLink}
                    to={item.to}
                    variant="outlined"
                    sx={{
                      justifyContent: "flex-start",
                      borderColor: style.accentGreen,
                      color: style.color4,
                      fontWeight: 700,
                      "&:hover": {
                        borderColor: style.color4,
                        backgroundColor: "rgba(255,255,255,0.04)",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Paper>
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5, mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: style.darkPanel,
                  boxShadow: 5,
                  minHeight: 50,
                  maxHeight:75,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1.1} alignItems="flex-start" >
                  <DownloadRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.2rem" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.25 }}>
                      Downloads
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.45 }}>
                      Installers, updates, and account access.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: style.darkPanel,
                  boxShadow: 5,
                  minHeight: 50,
                  maxHeight:75,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1.1} alignItems="flex-start">
                  <HeadphonesRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.2rem" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.25 }}>
                      Compatibility
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.45 }}>
                      DAW support, platforms, and setup.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: style.darkPanel,
                  boxShadow: 5,
                  minHeight: 50,
                  maxHeight:75,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1.1} alignItems="flex-start">
                  <AutoAwesomeRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.2rem" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.25 }}>
                      Subscription
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.45 }}>
                      Trials, billing, and cancellations.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: style.darkPanel,
                  boxShadow: 5,
                  minHeight: 50,
                  maxHeight:75,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1.1} alignItems="flex-start">
                  <SecurityRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.2rem" }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.25 }}>
                      Activation
                    </Typography>
                    <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.45 }}>
                      Sign-in, unlocking, and offline use.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Stack spacing={1.5}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                disableGutters
                sx={{
                  boxShadow: 8,
                  backgroundColor: faqRow,
                  color: style.color4,
                  borderRadius: "14px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: style.accentGreen }} />}
                  sx={{
                    minHeight: 70,
                    px: 2,
                    backgroundColor: "rgba(0,0,0,0.12)",
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <QuizRoundedIcon sx={{ color: style.accentGreen, fontSize: "1.15rem" }} />
                    <Typography
                      sx={{
                        fontSize: { xs: "1rem", md: "1.08rem" },
                        fontWeight: 700,
                        color: style.color4,
                        lineHeight: 1.4,
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: 2,
                    py: 2,
                    backgroundColor: style.darkerPanel,
                    color: style.color4,
                  }}
                >
                  {faq.richAnswer}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          <Paper
            sx={{
              mt: 3,
              p: 2.25,
              borderRadius: 3,
              background: style.darkPanel,
              boxShadow: 8,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                  Still need help?
                </Typography>
                <Typography sx={{ color: style.color3, lineHeight: 1.6 }}>
                  If you do not see your question here, reach out and we will be
                  happy to help.
                </Typography>
              </Stack>

              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                sx={{
                  backgroundColor: style.accentGreen,
                  color: "#fff",
                  fontWeight: 700,
                  px: 2.25,
                  py: 1.15,
                  "&:hover": {
                    filter: "brightness(1.08)",
                    backgroundColor: style.accentGreen,
                  },
                }}
              >
                Contact Support
              </Button>
            </Stack>
          </Paper>
        </Paper>
      </Box>
    </>
  );
}